import { List, Record } from "immutable";
import { checkDate } from "common/helpers/Date";
import { DateStringNumber } from ".";

export type ValidationKind = "default" | "inspection" | "survey";

// ---- Definition category ----

export interface IValidationDefinitionCategoryRecord {
  name: string;
  product_uuids: List<string>;
}

const defaultCategoryProps: IValidationDefinitionCategoryRecord = {
  name: "",
  product_uuids: List(),
};

export class ValidationDefinitionCategoryRecord
  extends Record<IValidationDefinitionCategoryRecord>(defaultCategoryProps)
  implements IValidationDefinitionCategoryRecord {}

export const validationDefinitionCategoryFromJSON = (
  json: IValidationDefinitionCategory,
): ValidationDefinitionCategoryRecord =>
  new ValidationDefinitionCategoryRecord({
    name: json.name,
    product_uuids: List(json.product_uuids),
  });

// Raw JSON shape (arrays, not Lists)
export interface IValidationDefinitionCategory {
  name: string;
  product_uuids: string[];
}

// ---- Definition ----

export interface IValidationDefinitionRecord {
  required_categories: List<ValidationDefinitionCategoryRecord>;
  required_categories_message?: string;
  categories: List<ValidationDefinitionCategoryRecord>;
  categories_message?: string;
  min_required_qty?: number;
  photo_upload_required?: boolean;
  photo_upload_message?: string;
  kind?: "product_option_group";
  required_selection_count?: number;
  required_selection_count_message?: string;
}

const defaultDefinitionProps: IValidationDefinitionRecord = {
  required_categories: List(),
  required_categories_message: undefined,
  categories: List(),
  categories_message: undefined,
  min_required_qty: undefined,
  photo_upload_required: undefined,
  photo_upload_message: undefined,
  kind: undefined,
  required_selection_count: undefined,
  required_selection_count_message: undefined,
};

export class ValidationDefinitionRecord
  extends Record<IValidationDefinitionRecord>(defaultDefinitionProps)
  implements IValidationDefinitionRecord {}

export const validationDefinitionFromJSON = (json: Partial<IValidationDefinition>): ValidationDefinitionRecord =>
  new ValidationDefinitionRecord({
    ...json,
    required_categories: List((json.required_categories ?? []).map(validationDefinitionCategoryFromJSON)),
    categories: List((json.categories ?? []).map(validationDefinitionCategoryFromJSON)),
  });

// Raw JSON shape
export interface IValidationDefinition {
  required_categories?: IValidationDefinitionCategory[];
  required_categories_message?: string;
  categories?: IValidationDefinitionCategory[];
  categories_message?: string;
  min_required_qty?: number;
  photo_upload_required?: boolean;
  photo_upload_message?: string;
  kind?: "product_option_group";
  required_selection_count?: number;
  required_selection_count_message?: string;
}

// ---- Validation rule record ----

export interface IValidationData {
  id: number;
  name: string;
  item_uuid: string;
  kind: ValidationKind;
  definition: IValidationDefinition;
  org_id: number;
  created_at: DateStringNumber;
  updated_at: DateStringNumber;
}

export interface IValidationRecord {
  id: number;
  name: string;
  item_uuid: string;
  kind: ValidationKind;
  definition: ValidationDefinitionRecord;
  org_id: number;
  created_at: Date;
  updated_at: Date;
}

const defaultValidationProps: IValidationRecord = {
  id: 0,
  name: "",
  item_uuid: "",
  kind: "default",
  definition: new ValidationDefinitionRecord(),
  org_id: 0,
  created_at: new Date(),
  updated_at: new Date(),
};

export class ValidationRecord extends Record<IValidationRecord>(defaultValidationProps) implements IValidationRecord {}

export const validationFromJSON = (json: Partial<IValidationData>): ValidationRecord => {
  const data: IValidationRecord = {
    ...(json as any),
    definition: validationDefinitionFromJSON(json.definition ?? {}),
  };

  if (json.created_at) {
    data.created_at = checkDate(json.created_at);
  }

  if (json.updated_at) {
    data.updated_at = checkDate(json.updated_at);
  }

  return new ValidationRecord(data);
};
